import { captureException } from '@sentry/react';
import classNames from 'classnames';
import { useEffect } from 'react';
import { trackFlexipakkenMoreInformationEvent } from '~/analytics/eventTracker';
import useModal from '~/components/modal/Modal';
import { DEFAULT_TV_CHANNEL_IDS } from '~/components/routes/campaign/constants';
import { useBasket } from '~/components/routes/campaign/useBasket';
import { Button, Icon, Loader } from '~/components/shared';
import ErrorLabel from '~/components/shared/error-label/ErrorLabel';
import type { Channel } from '~/models/ChannelModel';
import { ProductType } from '~/models/product/ProductType';
import { useActiveChannels } from '~/store/campaign/api';
import { ChannelPreviewDialog } from './ChannelPreviewDialog';
import styles from './FlexipakkenChoice.module.css';
import { FlexiPakkenInfoDialog } from './FlexiPakkenInfoDialog';

const STREAMING_WEIGHT = 6;
const CHANNEL_WEIGHT = 1;

const TV_CHANNELS_LABEL = '6 ekstra TV-kanaler';
const radioGroup = 'flexipakken-addon';
const groupLabelId = 'flexipakken-group-label';
const errorId = 'flexipakken-error';

type FlexipakkenSelection = number | 'tv-channels';

export const FlexipakkenChoice = () => {
  const { open } = useModal();
  const selectedProducts = useBasket((s) => s.selectedProducts);
  const flexipakkenChoice = useBasket((s) => s.flexipakkenChoice);
  const setFlexipakkenChoice = useBasket((s) => s.setFlexipakkenChoice);
  const stepError = useBasket((s) => s.stepError);
  const setStepError = useBasket((s) => s.setStepError);
  const commercialProductId =
    selectedProducts.find((p) => p.productType === ProductType.Subscription)?.offer.commercialProductId ?? 0;
  const { data: channels, error, isLoading, mutate } = useActiveChannels(commercialProductId, true);

  const streamingOptions = channels?.filter((c) => c.weight === STREAMING_WEIGHT) ?? [];
  const tvChannels = channels?.filter((c) => c.weight === CHANNEL_WEIGHT) ?? [];
  const previewChannels = DEFAULT_TV_CHANNEL_IDS.flatMap(
    (id) => tvChannels.find((c) => c.channelId === id) ?? [],
  );

  const effectiveSelected: FlexipakkenSelection | null =
    flexipakkenChoice?.kind === 'tv-channels'
      ? 'tv-channels'
      : (flexipakkenChoice?.channelId ?? null);

  const selectStreaming = (ch: Channel) => {
    setFlexipakkenChoice({
      kind: 'streaming',
      channelId: ch.channelId,
      name: ch.name,
    });
    if (stepError) setStepError(undefined);
  };
  const selectTvChannels = () => {
    setFlexipakkenChoice({ kind: 'tv-channels', name: TV_CHANNELS_LABEL });
    if (stepError) setStepError(undefined);
  };

  const hasError = !!stepError && !flexipakkenChoice;

  useEffect(() => {
    if (error) captureException(error, { fingerprint: ['flexipakken-channels-load'] });
  }, [error]);

  if (isLoading) {
    return (
      <section className={classNames('card', styles.card)}>
        <Loader />
      </section>
    );
  }

  if (error) {
    return (
      <section className={classNames('card', styles.card)} role="alert">
        <h3 className={classNames('title-3', styles.title)}>Inkludert innhold</h3>
        <p>Vi klarte ikke å laste inn alternativene. Prøv igjen.</p>
        <button type="button" onClick={() => mutate()}>
          Prøv igjen
        </button>
      </section>
    );
  }

  return (
    <section className={classNames('card', styles.card)}>
      <div className={styles.infoHeader}>
        <h3 className={classNames('title-3', styles.title)}>Inkludert innhold</h3>
        <p className={styles.description}>
          I FleksiPakken velger du selv hva som skal være inkludert.
          Velg ett av alternativene nå, så kan du enkelt endre valget ditt på Min side senere.
        </p>
        <Button
          type="tertiary"
          className={styles.information}
          iconMargin='left'
          onClick={() => {
            trackFlexipakkenMoreInformationEvent('how-it-works');
            open(<FlexiPakkenInfoDialog />);
          }}
          disabled={tvChannels.length === 0}
        >
          <Icon type="questionCircleBold"  />
          Hvordan fungerer dette?
        </Button>
      </div>

      <hr className={styles.divider} />
      <p className={styles.sectionTitle} id={groupLabelId}>Hva vil du starte med?</p>

      <div
        className={styles.optionsGrid}
        role="radiogroup"
        aria-labelledby={groupLabelId}
        aria-describedby={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        aria-required="true"
      >
        {/* Left: TV channels option */}
        <label
          className={classNames(styles.option, styles.tvOption, {
            [styles.active]: effectiveSelected === 'tv-channels',
          })}
        >
          <span className={styles.optionHeader}>
            <input
              type="radio"
              name={radioGroup}
              value="tv-channels"
              checked={effectiveSelected === 'tv-channels'}
              onChange={selectTvChannels}
            />
            <span className={styles.optionLabel}>{TV_CHANNELS_LABEL}</span>
          </span>
          <p className={styles.optionDescription}>
            Du starter med disse 6 kanalene. Du kan enkelt endre innholdet på Min side etter at bestillingen er klar.
          </p>
          <hr className={styles.divider} />
          <p className={styles.channelsSectionLabel}>Disse 6 kanalene følger med nå</p>
          <ul className={styles.channelLogos}>
            {previewChannels.map((ch, i) => (
              ch.logoImageUrl ? (
                <li key={ch.channelId || i} className={styles.channelLogo}>
                  <img
                    src={ch.logoImageUrl}
                    alt={ch.name}
                  />
                </li>
              ) : (
                <li key={ch.channelId || i} className={styles.channelLogoFallback} aria-hidden="true">
                  {ch.name.slice(0, 2).toUpperCase()}
                </li>
              )
            ))}
          </ul>
          <Button
            type="link-style"
            className={styles.previewButton}
            // Nested in <label>: prevent the label from also toggling the radio.
            onClick={(e) => {
              e.preventDefault();
              trackFlexipakkenMoreInformationEvent('all-tv-channels');
              open(<ChannelPreviewDialog channels={tvChannels} />);
            }}
            disabled={tvChannels.length === 0}
          >
            Se alle TV-kanaler
          </Button>
        </label>

        {/* Right: Streaming options stacked */}
        <div className={styles.streamingStack}>
          {streamingOptions.map((ch) => (
            <label
              key={ch.channelId}
              className={classNames(styles.option, styles.streamingOption, {
                [styles.active]: effectiveSelected === ch.channelId,
              })}
            >
              <input
                type="radio"
                name={radioGroup}
                value={ch.channelId}
                checked={effectiveSelected === ch.channelId}
                onChange={() => selectStreaming(ch)}
              />
              <span className={styles.optionLabel}>{ch.name}</span>
              {ch.logoImageUrl ? (
                <img src={ch.logoImageUrl} alt={ch.name} className={styles.streamingLogo} />
              ) : (
                <div className={classNames(styles.streamingLogo, styles.channelLogoFallback)} aria-hidden="true">
                  {ch.name.slice(0, 2).toUpperCase()}
                </div>
              )}
            </label>
          ))}
        </div>
      </div>
      <ErrorLabel id={errorId} hasError={hasError} focusOnError={true}>
        <span role="alert">{stepError}</span>
      </ErrorLabel>
    </section>
  );
};
